import React, { useState, useEffect, useRef } from 'react';
import { 
  X, 
  Loader2, 
  AlertCircle,
  ChevronRight,
  MapPin,
  Calendar,
  Camera,
  Bus,
  DollarSign,
  FileText,
  CheckCircle
} from 'lucide-react';
import { TRIP_ID } from '../../constants/tripIds';
import { apiService } from '../../services/api';

// Ordered list of trips for Fast Reply popup
const ORDERED_TRIPS = [
  'KODAIKANAL',
  'CHIKMANGLUR',
  'WAYNAD',
  'GOKARNA',
  'COORG',
  'OOTY_RESORT_STAY',
  'LEPAKSHI',
  'KOTI',
  'HOGENAKKAL_FALL',
  'DD_HILLS',
  'MYSURU',
  'UTTARI_BETTA',
  'NANDI_HILLS',
  'GUDIBANDE'
] as const;

// Submenu items per trip
const SUBMENUS = [
  { key: "ITINERARY", label: "Itinerary", icon: FileText },
  { key: "UPCOMING_DATES", label: "Upcoming Dates", icon: Calendar },
  { key: "PICKUP_LOCATIONS", label: "Pickup Locations", icon: MapPin },
  { key: "INCLUSION", label: "Inclusion", icon: CheckCircle },
  { key: "ADDITIONAL_COST", label: "Additional Cost", icon: DollarSign },
  { key: "HOTEL_PHOTOS", label: "Hotel Photos", icon: Camera },
  { key: "BUS_PHOTOS", label: "Bus Photos", icon: Bus }
];

// Mock mode flag - for development testing
// Set to true to enable mock mode (no real API calls)
const USE_MOCK = false; // Changed to false to use real API

interface FastReplyProps {
  userId: number;
  isOpen: boolean;
  onClose: () => void;
}

export const FastReply: React.FC<FastReplyProps> = ({
  userId,
  isOpen,
  onClose
}) => {
  console.log('FastReply component rendered with props:', { userId, isOpen });
  
  const [selectedTrip, setSelectedTrip] = useState<string>('');
  const [selectedSubmenu, setSelectedSubmenu] = useState<string>('');
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const popupRef = useRef<HTMLDivElement>(null);
  const firstButtonRef = useRef<HTMLButtonElement>(null);

  // Focus management and keyboard navigation
  useEffect(() => {
    if (isOpen) {
      // Focus trap
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          onClose();
        }
      };

      document.addEventListener('keydown', handleKeyDown);
      
      // Focus first button when opened
      setTimeout(() => {
        firstButtonRef.current?.focus();
      }, 100);

      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [isOpen, onClose]);

  // Reset state when popup opens/closes
  useEffect(() => {
    if (isOpen) {
      setSelectedTrip('');
      setSelectedSubmenu('');
      setError(null);
    }
  }, [isOpen]);

  // Mock API call for development
  const mockSendMessage = async (messageData: any): Promise<any> => {
    console.log('mockSendMessage called with:', messageData);
    return new Promise((resolve, reject) => {
      console.log('Starting mock delay...');
      setTimeout(() => {
        console.log('Mock delay completed, checking success...');
        // Simulate random success/failure for testing
        if (Math.random() > 0.1) { // 90% success rate
          console.log('Mock API success');
          resolve({
            ...messageData,
            id: `mock_${Date.now()}`,
            status: 'sent',
            timestamp: new Date().toISOString()
          });
        } else {
          console.log('Mock API failure');
          reject(new Error('Mock API failure'));
        }
      }, 1000 + Math.random() * 2000); // 1-3 second delay
    });
  };

  // Real API call
  const sendFastReplyMessage = async (messageData: any): Promise<any> => {
    console.log('sendFastReplyMessage called with:', messageData);
    console.log('userId:', userId);
    const result = await apiService.sendFastReplyMessage(userId, messageData);
    console.log('API service result:', result);
    return result;
  };

  const handleSubmenuClick = async (submenuKey: string) => {
    console.log('=== handleSubmenuClick START ===');
    console.log('handleSubmenuClick called with:', submenuKey);
    console.log('selectedTrip:', selectedTrip);
    console.log('isSending:', isSending);
    
    if (!selectedTrip) {
      console.log('No trip selected, showing error');
      setError('Please select a trip first');
      return;
    }

    console.log('Setting selectedSubmenu and starting API call...');
    setSelectedSubmenu(submenuKey);
    setIsSending(true);
    setError(null);

    const messageData = {
      message: `${submenuKey} for ${selectedTrip}`,
      type: 'QuickReply',
      action: submenuKey,
      inventoryId: TRIP_ID[selectedTrip as keyof typeof TRIP_ID]
    };

    console.log('Fast Reply Message Data:', {
      selectedTrip,
      selectedSubmenu: submenuKey,
      inventoryId: messageData.inventoryId,
      action: messageData.action,
      fullMessageData: messageData
    });

    try {
      console.log('Making API call...', USE_MOCK ? 'MOCK' : 'REAL');
      console.log('USE_MOCK value:', USE_MOCK);
      
      // Send actual message
      const result = USE_MOCK 
        ? await mockSendMessage(messageData)
        : await sendFastReplyMessage(messageData);

      console.log('API call successful:', result);
      
      // Close modal after successful API call
      console.log('Closing modal...');
      onClose();

    } catch (err: any) {
      console.error('=== API call failed ===');
      console.error('Error details:', err);
      console.error('Error message:', err.message);
      console.error('Error stack:', err.stack);
      
      setError(err.message || 'Failed to send message');
    } finally {
      console.log('Setting isSending to false');
      setIsSending(false);
    }
    
    console.log('=== handleSubmenuClick END ===');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
      <div 
        ref={popupRef}
        className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-hidden"
        role="dialog"
        aria-labelledby="fast-reply-title"
        aria-describedby="fast-reply-description"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 id="fast-reply-title" className="text-lg font-semibold text-gray-900">
            Fast Reply
          </h2>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
            aria-label="Close fast reply"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          <p id="fast-reply-description" className="text-sm text-gray-600">
            Send a quick reply with trip information
          </p>

          {/* Trip Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Trip
            </label>
            <select
              value={selectedTrip}
              onChange={(e) => setSelectedTrip(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              disabled={isSending}
            >
              <option value="">Choose a trip...</option>
              {ORDERED_TRIPS.map((tripName) => (
                <option key={tripName} value={tripName}>
                  {tripName.replace(/_/g, ' ')} ({TRIP_ID[tripName as keyof typeof TRIP_ID]})
                </option>
              ))}
            </select>
            {selectedTrip && (
              <p className="text-xs text-gray-500 mt-1">
                Trip ID: <span className="font-mono">{TRIP_ID[selectedTrip as keyof typeof TRIP_ID]}</span>
              </p>
            )}
          </div>

          {/* Submenu Selection */}
          {selectedTrip && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Information Type
              </label>
              <div className="grid grid-cols-1 gap-2">
                {SUBMENUS.map((submenu) => {
                  const IconComponent = submenu.icon;
                  return (
                    <button
                      key={submenu.key}
                      ref={submenu.key === SUBMENUS[0].key ? firstButtonRef : undefined}
                      onClick={() => {
                        console.log('Submenu button clicked:', submenu.key);
                        handleSubmenuClick(submenu.key);
                      }}
                      className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                        selectedSubmenu === submenu.key
                          ? 'border-primary-500 bg-primary-50 text-primary-700'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      } ${isSending ? 'opacity-50 cursor-not-allowed' : ''}`}
                      onMouseEnter={() => console.log('Mouse entered button:', submenu.key)}
                      onMouseLeave={() => console.log('Mouse left button:', submenu.key)}
                      onMouseDown={() => console.log('Mouse down on button:', submenu.key)}
                      onMouseUp={() => console.log('Mouse up on button:', submenu.key)}
                    >
                      <div className="flex items-center space-x-3">
                        <IconComponent className="h-4 w-4" />
                        <span className="text-sm font-medium">{submenu.label}</span>
                      </div>
                      {isSending && selectedSubmenu === submenu.key ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="flex items-center space-x-2 p-3 bg-red-50 border border-red-200 rounded-lg">
              <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
              <span className="text-sm text-red-700">{error}</span>
            </div>
          )}

          {/* Mock Mode Indicator */}
          {USE_MOCK && (
            <div className="flex items-center space-x-2 p-2 bg-yellow-50 border border-yellow-200 rounded-lg">
              <AlertCircle className="h-4 w-4 text-yellow-500" />
              <span className="text-xs text-yellow-700">Mock Mode Active</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
            disabled={isSending}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
